/**
 * CMS: Colorado Pilot Microsite Application
 */

// Import design tokens CSS (must be first for Vite to process correctly)
import '@ignite/web/index.css';

// Development mode shorthand
const DEV = import.meta.env.DEV;

// Measure FOUC timing (dev only)
const startTime = DEV ? performance.now() : null;

// Register FontAwesome icons used in the application
import { registerIcons } from '@ignite/web/utils/icon-resolver.js';
import * as icons from './icons.js';

registerIcons(icons);

// Import Xcel Energy Web Components (for custom element registration)
import * as components from './components.js';

// Import GA4 tracking (initializes automatically)
// Note: GA4 gtag.js script is loaded in index.html <head>
import './ga4-tracking.js';

// Log when components are defined
Promise.all([components])
  .then(() => {
    if (DEV) {
      const endTime = performance.now();
      const upgradeTime = endTime - startTime;
      console.log(`⚡ Web components upgraded in ${upgradeTime.toFixed(2)}ms`);
    }
});

// Scroll to hash on page load (if present)
if (window.location.hash) {
  // Wait for DOM to be ready
  const scrollToHash = () => {
    const target = document.querySelector(window.location.hash);
    if (target) {
      // Small delay to ensure layout is settled
      setTimeout(() => {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scrollToHash);
  } else {
    scrollToHash();
  }
}

// Smooth scroll for anchor links
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault();
    const hash = this.getAttribute('href');
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      // Update URL hash after smooth scroll (preserves position on refresh)
      history.pushState(null, '', hash);
    }
  });
});

// Handle back/forward button navigation with hash changes
window.addEventListener('hashchange', () => {
  if (!window.location.hash) {
    // Back to root - scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    // Navigate to hash section
    const target = document.querySelector(window.location.hash);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
});


// Initialize settings panel (dev and qa environments - includes theme toggle)
// Enable based on environment detection:
// - Local dev: import.meta.env.DEV
// - QA/Dev deployments: hostname contains '.dev' or '.qa'
// - Production: disabled (cms-pilot.xcelenergy.com)
const isNonProduction = DEV ||
  window.location.hostname.includes('.dev') ||
  window.location.hostname.includes('.qa') ||
  window.location.hostname.includes('localhost');

if (isNonProduction) {
  import('./non-prod/settingsPanel.js').then(({ createSettingsPanel }) => {
    createSettingsPanel();
    console.log('✓ App initialized');
  });
}
